import { motion } from "motion/react";
import { Button } from "@/components/landing/button";
import { Pill } from "@/components/ui/pill";
import { Link } from "@/components/landing/link";
import { Phone } from "@/components/landing/phone";
import { prepareQuizStart } from "../../analytics";
import type { Variant } from "../../experiments";

type Props = {
  variant: Variant<"landing-message">;
  resolved: boolean;
};

export function Hero({ variant, resolved }: Props) {
  return (
    <div className="z-1 grid w-full place-items-center">
      <div className="mt-16 flex flex-col items-center gap-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
        >
          <Pill>
            <p className="px-2 text-xs font-medium sm:text-sm">Private by design · Built for iPhone</p>
          </Pill>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          className="text-center text-4xl leading-[1.1] font-semibold tracking-tighter sm:text-7xl"
          aria-busy={!resolved}
        >
          {variant === "appointment" ? (
            <>
              Walk into your next appointment.<span className="block text-tint">Ready.</span>
            </>
          ) : (
            <>
              Your GLP-1 history.<span className="block text-tint">Finally clear.</span>
            </>
          )}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
          className="max-w-lg text-center leading-6 tracking-tight sm:text-xl"
        >
          Log what actually happened, notice your own patterns, and carry a clear treatment history
          into care.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
          className="flex flex-col items-center gap-3"
        >
          <Button className="w-fit" variant="tint" size="lg" asChild>
            <Link
              href="/quiz"
              onClick={() =>
                prepareQuizStart({ placement: "hero", experiment: "landing-message", variant })
              }
            >
              Build my journal
            </Link>
          </Button>
          <p className="mb-10 text-xs text-muted-foreground">
            No account · Answers stay in this browser tab
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.7,
            delay: 0.5,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        >
          <Phone />
        </motion.div>
      </div>
    </div>
  );
}
